import { BoxProps, FormControl } from "@chakra-ui/react";

import { debounce, get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormBuilder } from "../context/FormBuilderContext/context";

interface Props<T = any> {
  onChange(value: T): void;
  value: T;
  path: (string | number)[];
  isInvalid: boolean;
  formControlStyle?: BoxProps["style"];
  useDebounce?: boolean;
}

function withField<ComponentProps, ValueType = any>(
  Component: React.ComponentType<ComponentProps>,
  change: (e: any) => ValueType
) {
  return (props: Props<ValueType> & Omit<ComponentProps, "onChange">) => {
    const {
      useDebounce = true,
      isInvalid = true,
      path,
      formControlStyle = {},
      ...rest
    } = props;
    const [state, setState] = useState(props.value);

    const { errors } = useFormBuilder();

    const debounceChangeRef = useRef(debounce(props.onChange, 300));
    const changeRef = useRef(props.onChange);
    changeRef.current = props.onChange;

    const hasError = useMemo(
      () => !!get(errors, path.join(".")),
      [errors, path]
    );

    useEffect(() => {
      setState(props.value);
    }, [props.value]);

    const onChange = useCallback(
      (value: any) => {
        setState(value);
        if (useDebounce) {
          debounceChangeRef.current(value);
        } else {
          changeRef.current(value);
        }
      },
      [useDebounce]
    );

    return (
      <FormControl
        isInvalid={hasError && isInvalid}
        style={formControlStyle}
        scrollMarginTop="60px"
      >
        <Component
          {...(rest as ComponentProps)}
          value={state}
          onChange={(e: any) => onChange(change(e))}
        />
      </FormControl>
    );
  };
}

export default withField;
